import React, { useState, useEffect } from 'react';
import { Buffer } from 'buffer';
import { createClient, gtv, IClient,  } from 'postchain-client';
import {
  createAmount,
  createWeb3ProviderEvmKeyStore,
  Account,
  Balance,
  Session,
  transfer,
  ftEventEmitter,
  Asset,
  pendingTransferStrategies,
  AuthFlag,
  createSingleSigAuthDescriptorRegistration,
  registerAccount,
  getAssetsByName,
  registrationStrategy,
  EvmKeyStore,
  login,
} from '@chromia/ft4';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { useChromiaSession, createEClient, loginUsingKeyStoreInteractor } from './hooks/useChromiaSession.ts';

declare global {
  interface Window { ethereum: any }
}

interface TransferRecord {
  receiverId: string;
  amount: number;
}

const isEthereumAddress = (address: string): boolean => {
  return /^0x[0-9a-fA-F]{1,40}$/.test(address);
};

const padTo20Bytes = (buffer: Buffer): Buffer => {
  if (buffer.length >= 20) return buffer;
  const paddedBuffer = Buffer.alloc(20);
  buffer.copy(paddedBuffer, 20 - buffer.length);
  return paddedBuffer;
};

const ethereumToChromiaAccountId = (ethAddress: string): Buffer => {
  const addressWithoutPrefix = ethAddress.substring(2);
  const addressBuffer = Buffer.from(addressWithoutPrefix, 'hex');
  const paddedBuffer = padTo20Bytes(addressBuffer);
  return gtv.gtvHash(paddedBuffer);
};

function createAccount(client: IClient, keyStore: EvmKeyStore, strategy: string, feeAsset: Asset) {
    const authDescriptor = createSingleSigAuthDescriptorRegistration(
      [AuthFlag.Account, AuthFlag.Transfer],
      keyStore.id
    );
    
    if (strategy === "open") {
      return registerAccount(
        client,
        keyStore,
        registrationStrategy.transferOpen(authDescriptor)
      );
    } else if (strategy === "fee") {
      return registerAccount(
        client,
        keyStore,
        registrationStrategy.transferFee(feeAsset, authDescriptor)
      );
    } else {
      throw new Error("Invalid registration strategy");
    }
}

function App() {
  const queryParams = new URLSearchParams(window.location.search);
  const storageType = queryParams.get('storageType') || 'session';

  const { session, accounts, ethAddress, provisionalAccountId, strategyType, keyStoreInteractor } = useChromiaSession(storageType);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [csvData, setCsvData] = useState('');
  const [copied, setCopied] = useState(false);
  const [transferMsg, setTransferMsg] = useState({ success: false, message: '', txId: '' });
  const [open, setOpen] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    const getBalances = async () => {
      setBalances([]);
      if (session) {
        try {
          console.log("Fetching balances for account", session.account.id);
          const balancesData = await session.account.getBalances();
          setBalances(balancesData.data);
        } catch (error) {
          console.error("Error fetching balances:", error);
        }
      }
    };

    getBalances();
  }, [session, ethAddress]);

  const handleCopy = (hexId: string) => {
    navigator.clipboard.writeText(hexId);
    setCopied(true);
  };

  const parseCSV = (csvText: string): TransferRecord[] => {
    return csvText
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        const [receiverId, amount] = line.split(',').map(s => s.trim());
        return {
          receiverId: isEthereumAddress(receiverId) 
            ? ethereumToChromiaAccountId(receiverId).toString('hex')
            : receiverId,
          amount: parseFloat(amount)
        };
      });
  };

  const handleTransfer = async () => {
    setIsTransferring(true);
    try {
      if (session === null) throw new Error('Session is not initialized');
      
      const transfers = parseCSV(csvData);
      if (transfers.length === 0) {
        throw new Error('No valid transfers found in CSV data');
      }

      const mySession = (transfers.length > 1) ?
        await loginUsingKeyStoreInteractor(keyStoreInteractor, 
          accounts[0].id) : session;

      const tb = mySession.transactionBuilder();
      
      transfers.forEach(({ receiverId, amount }) => {
        tb.add(transfer(
          Buffer.from(receiverId, 'hex'),
          balances[0].asset.id,
          createAmount(amount, balances[0].asset.decimals)
        ));
      });

      const { tx, receipt } = await tb.buildAndSend()
        .on('built', (builtTx) => {
          console.log('Transaction built:', builtTx);
        })
        .on('sent', (txRid) => {
          console.log('Transaction sent, RID:', txRid.toString('hex'));
        });

      setTransferMsg({
        success: true,
        message: `Batch transfer successful! ${transfers.length} transfers completed.`,
        txId: receipt.transactionRid.toString('hex')
      });
    } catch (error) {
      setTransferMsg({
        success: false,
        message: `Transfer failed! Reason: ${error}`,
        txId: ''
      });
    } finally {
      setIsTransferring(false);
      setOpen(true);
    }
  };

  const handleClose = (_, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleCreateAccount = async () => {
    setIsCreatingAccount(true);
    setAccountError(null);
    
    try {
      const client = await createEClient();

      const store = await createWeb3ProviderEvmKeyStore(window.ethereum);
      const chrAsset = (await getAssetsByName(client, "Chromia")).data[0];
      
      if (!strategyType) {
        throw new Error("No valid registration strategy available");
      }

      const result = await createAccount(client, store, strategyType, chrAsset);
      console.log("Account creation transaction:", result);
      
      if (result) {
        setTransferMsg({
          success: true,
          message: "Account created successfully!",
          txId: "TODO"
        });
        setOpen(true);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      console.error("Failed to create account:", error);
      setAccountError(error instanceof Error ? error.message : "Failed to create account");
      setOpen(true);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Typography variant="h3" gutterBottom>
          Chromia Batch Transfer
        </Typography>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Mainnet:Economy Chain
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="text.secondary">
                Ethereum Address
              </Typography>
              <Typography variant="h6">
                {ethAddress || <CircularProgress size={20} />}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {accounts.length ? (
                accounts.map((account, index) => {
                  const hexId = Buffer.from(account.id).toString('hex');
                  return (
                    <Box key={index} mb={2}>
                      <Typography variant="subtitle1" color="text.secondary">
                        Chromia Account
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleCopy(hexId)}
                      >
                        {hexId}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <Box>
                  <Typography variant="subtitle1" color="text.secondary">
                    Provisional Account ID
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => handleCopy(provisionalAccountId)}
                  >
                    {provisionalAccountId}
                  </Typography>
                  
                  {!accounts.length && strategyType && (
                    <Button
                      variant="contained"
                      onClick={handleCreateAccount}
                      disabled={isCreatingAccount}
                      sx={{ mt: 2 }}
                    >
                      {isCreatingAccount ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Creating Account...
                        </>
                      ) : (
                        `Create Account (${strategyType})`
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              {balances.length ? (
                balances.map((balance, index) => (
                  <Box key={index} mb={2}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {balance.asset.name} Balance
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h6">
                        {balance.amount.toString()}
                      </Typography>
                      <img 
                        src={balance.asset.iconUrl} 
                        alt={balance.asset.name} 
                        style={{ height: '30px' }} 
                      />
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No balances found</Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Batch Transfer
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Format: receiverId,amount (one per line)
          </Typography>
          <TextField
            label="CSV Data"
            variant="outlined"
            multiline
            rows={4}
            value={csvData}
            onChange={e => setCsvData(e.target.value)}
            sx={{ mb: 2, width: '100%' }}
            placeholder="receiverId,amount&#10;receiverId,amount&#10;..."
          />
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={!session || !csvData || !balances.length || isTransferring}
            sx={{ fontSize: '1.1rem' }}
          >
            {isTransferring ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Transferring...
              </>
            ) : (
              'Transfer'
            )}
          </Button>
        </Paper>

        <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
          <Alert 
            onClose={handleClose} 
            severity={transferMsg.success ? "success" : "error"} 
            sx={{ width: '100%' }}
          >
            {transferMsg.message}
            {transferMsg.txId && (
              <Box sx={{ mt: 1, wordBreak: 'break-all' }}>
                Transaction ID: {transferMsg.txId}
              </Box>
            )}
          </Alert>
        </Snackbar>
        <Snackbar open={!!accountError} autoHideDuration={6000} onClose={() => setAccountError(null)}>
          <Alert onClose={() => setAccountError(null)} severity="error" sx={{ width: '100%' }}>
            {accountError}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default App;
